// src/components/PaymentModal.tsx
// Web equivalent of the apps' Stripe PaymentSheet — uses the same backend params
// (clientSecret + publishableKey) with Stripe.js Elements.
import React, { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { X } from "lucide-react";

export type CheckoutParams = {
  clientSecret: string;
  publishableKey: string;
  formattedPrice?: string;
  [k: string]: any;
};

function InnerForm({
  title, subtitle, payLabel, onSuccess, onClose,
}: {
  title: string;
  subtitle?: string;
  payLabel: string;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setErr(null);
    const { error } = await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (error) {
      setErr(error.message || "Payment failed");
      setBusy(false);
      return;
    }
    setBusy(false);
    onSuccess();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
      </div>
      {subtitle && <div className="text-sm text-gray-400 mb-4">{subtitle}</div>}

      <div className="mb-4">
        <PaymentElement />
      </div>

      {err && <div className="mb-3 text-sm text-red-400">{err}</div>}

      <button
        onClick={pay}
        disabled={!stripe || busy}
        className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 font-semibold text-white"
      >
        {busy ? "Processing…" : payLabel}
      </button>
      <div className="text-center text-xs text-gray-500 mt-3">Secured by Stripe</div>
    </div>
  );
}

export default function PaymentModal({
  checkout, title, subtitle, payLabel = "Pay", onSuccess, onClose,
}: {
  checkout: CheckoutParams;
  title: string;
  subtitle?: string;
  payLabel?: string;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const stripePromise = useMemo(() => loadStripe(checkout.publishableKey), [checkout.publishableKey]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-2xl p-5">
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: checkout.clientSecret, appearance: { theme: "night" } }}
        >
          <InnerForm
            title={title}
            subtitle={subtitle}
            payLabel={payLabel}
            onSuccess={onSuccess}
            onClose={onClose}
          />
        </Elements>
      </div>
    </div>
  );
}
